import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AFFILIATE_CODE_PATTERN, type AffiliateDto, type AffiliateInput, type AffiliatePayoutRequest, type AffiliateStatus, type CommissionStatus } from '@ecom/contracts';
import { AsyncSelect } from '@/components/async-select';
import { Choices } from '@/components/fields';
import { FilterBar } from '@/components/filters';
import { PlusIcon, UsersIcon } from '@/components/icons';
import { KeyValue, ListCard, ListToolbar, LoadMore, Page, Segmented, Sheet, Table, TableScroll, Td, Th, Tr } from '@/components/page';
import { Alert, Button, CopyButton, EmptyState, Field, Input, Pill, Select, TableSkeleton, Textarea, type PillTone } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import { count, date, money } from '@/lib/format';
import { couponOptions, customerOptions } from '@/lib/options';
import { bpsToPercent, percentToBps } from '@/lib/price-input';
import { storefrontUrl } from '@/lib/storefront-url';
import { useAffiliatesStore } from '@/stores/affiliates.store';

const COMMISSION_TONE: Record<CommissionStatus, PillTone> = { PENDING: 'warning', APPROVED: 'info', PAID: 'success', VOID: 'neutral' };
const PAYOUT_METHODS: AffiliatePayoutRequest['method'][] = ['BANK_TRANSFER', 'CASH', 'OTHER'];

/** The link an affiliate shares: any storefront page with their code on it. */
function referralLink(code: string): string {
  return storefrontUrl('ar', `/?ref=${code}`) ?? `/?ref=${code}`;
}

/**
 * Affiliates (ADR-084): who brings orders, through their link or their own
 * coupon; what those orders earned them; and what the store has paid — every
 * figure from the server, the list searched and paged there.
 */
export function AffiliatesPage() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { items, nextCursor, total, search, status, loading, error, load, loadMore, open, close, current, clearSaveError } = useAffiliatesStore();
  const [editing, setEditing] = useState<AffiliateDto | 'new' | null>(null);

  useEffect(() => {
    void load();
  }, [load]);

  const add = (
    <Button
      onClick={() => {
        clearSaveError();
        setEditing('new');
      }}
    >
      <PlusIcon className="size-4" />
      {t('affiliateAdd')}
    </Button>
  );

  return (
    <Page title={t('affiliates')} subtitle={t('affiliatesSubtitle')} actions={add} fill>
      <ListCard>
        <ListToolbar>
          <div className="flex flex-wrap items-center gap-3">
            <Segmented
              label={t('status')}
              items={[
                { id: '', label: t('giftCardsAll') },
                { id: 'ACTIVE', label: t('affiliateStatus_ACTIVE') },
                { id: 'PAUSED', label: t('affiliateStatus_PAUSED') },
              ]}
              value={status}
              onChange={(next) => void load({ status: next as typeof status })}
            />
            <div className="min-w-48 flex-1">
              <FilterBar search={search} onSearch={(term) => void load({ search: term })} placeholder={t('affiliatesSearch')} total={total ?? undefined} shown={items.length} />
            </div>
          </div>
        </ListToolbar>

        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading && items.length === 0 ? (
          <TableSkeleton columns={6} />
        ) : items.length === 0 ? (
          <EmptyState title={t('affiliatesEmpty')} body={t('affiliatesEmptyHint')} icon={<UsersIcon />} action={add} />
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t('affiliate')}</Th>
                  <Th>{t('affiliateCommission')}</Th>
                  <Th>{t('affiliateVisits')}</Th>
                  <Th>{t('orders')}</Th>
                  <Th>{t('affiliateSales')}</Th>
                  <Th>{t('affiliateOwed')}</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((affiliate) => (
                  <Tr key={affiliate.id} onClick={() => void open(affiliate.id)}>
                    <Td>
                      <span className="flex items-center gap-2">
                        <span className="font-medium text-ink">{affiliate.name}</span>
                        {affiliate.status === 'PAUSED' ? <Pill tone="neutral">{t('affiliateStatus_PAUSED')}</Pill> : null}
                      </span>
                      <code dir="ltr" className="text-xs text-ink-faint">
                        ?ref={affiliate.code}
                        {affiliate.coupon ? ` · ${affiliate.coupon.code}` : ''}
                      </code>
                    </Td>
                    <Td data-numeric className="text-ink">{bpsToPercent(affiliate.commissionBps)}%</Td>
                    <Td data-numeric className="text-ink-muted">{count(affiliate.stats.visits, locale)}</Td>
                    <Td data-numeric className="text-ink-muted">{count(affiliate.stats.orders, locale)}</Td>
                    <Td data-numeric className="whitespace-nowrap text-ink-muted">{money(affiliate.stats.sales, locale, affiliate.currency)}</Td>
                    <Td data-numeric className="whitespace-nowrap font-medium text-ink">{money(affiliate.stats.approved, locale, affiliate.currency)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}

        <LoadMore visible={Boolean(nextCursor)} loading={loading} onClick={() => void loadMore()} />
      </ListCard>

      {editing ? <EditSheet affiliate={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
      {current && !editing ? (
        <DetailSheet
          affiliate={current}
          onEdit={() => {
            clearSaveError();
            setEditing(current);
          }}
          onClose={close}
        />
      ) : null}
    </Page>
  );
}

function EditSheet({ affiliate, onClose }: { affiliate: AffiliateDto | null; onClose: () => void }) {
  const t = useT();
  const { save, saving, saveError } = useAffiliatesStore();
  const [name, setName] = useState(affiliate?.name ?? '');
  const [email, setEmail] = useState(affiliate?.email ?? '');
  const [code, setCode] = useState(affiliate?.code ?? '');
  const [rate, setRate] = useState(affiliate ? bpsToPercent(affiliate.commissionBps) : '10');
  const [status, setStatus] = useState<AffiliateStatus>(affiliate?.status ?? 'ACTIVE');
  const [discountId, setDiscountId] = useState(affiliate?.coupon?.id ?? '');
  const [userId, setUserId] = useState(affiliate?.account?.id ?? '');
  const [note, setNote] = useState(affiliate?.note ?? '');
  const bps = percentToBps(rate);
  const ready = name.trim() !== '' && AFFILIATE_CODE_PATTERN.test(code) && bps !== null && bps <= 10_000;

  async function submit() {
    if (!ready || bps === null) return;
    const input: AffiliateInput = {
      name: name.trim(),
      email: email.trim() || null,
      code,
      commissionBps: bps,
      status,
      discountId: discountId || null,
      userId: userId || null,
      note: note.trim() || null,
    };
    if (await save(affiliate?.id ?? null, input)) onClose();
  }

  return (
    <Sheet
      open
      title={affiliate ? affiliate.name : t('affiliateAdd')}
      subtitle={t('affiliateEditHint')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button disabled={!ready || saving} onClick={() => void submit()}>
            {t('save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('name')} htmlFor="affiliate-name">
            <Input id="affiliate-name" maxLength={120} value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
          <Field label={t('email')} htmlFor="affiliate-email">
            <Input id="affiliate-email" type="email" dir="ltr" value={email} onChange={(event) => setEmail(event.target.value)} />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('affiliateCode')} htmlFor="affiliate-code" hint={t('affiliateCodeHint')}>
            <Input
              id="affiliate-code"
              dir="ltr"
              maxLength={40}
              value={code}
              onChange={(event) => setCode(event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            />
          </Field>
          <Field label={t('affiliateCommissionPercent')} htmlFor="affiliate-rate" hint={t('affiliateCommissionHint')}>
            <Input id="affiliate-rate" dir="ltr" inputMode="decimal" value={rate} onChange={(event) => setRate(event.target.value)} />
          </Field>
        </div>
        <Field label={t('affiliateCoupon')} htmlFor="affiliate-coupon" hint={t('affiliateCouponHint')}>
          <AsyncSelect id="affiliate-coupon" value={discountId} onChange={(id) => setDiscountId(id)} fetchPage={couponOptions.page} fetchOne={couponOptions.one} emptyLabel={t('affiliateNone')} />
        </Field>
        <Field label={t('affiliateAccount')} htmlFor="affiliate-account" hint={t('affiliateAccountHint')}>
          <AsyncSelect id="affiliate-account" value={userId} onChange={(id) => setUserId(id)} fetchPage={customerOptions.page} fetchOne={customerOptions.one} emptyLabel={t('affiliateNone')} />
        </Field>
        <Choices
          legend={t('status')}
          name="affiliate-status"
          columns={2}
          value={status}
          onChange={setStatus}
          options={[
            { value: 'ACTIVE', label: t('affiliateStatus_ACTIVE') },
            { value: 'PAUSED', label: t('affiliateStatus_PAUSED'), hint: t('affiliatePausedHint') },
          ]}
        />
        <Field label={t('notes')} htmlFor="affiliate-note">
          <Textarea id="affiliate-note" rows={2} maxLength={1000} value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
        {saveError ? <Alert>{saveError}</Alert> : null}
      </div>
    </Sheet>
  );
}

function DetailSheet({ affiliate, onEdit, onClose }: { affiliate: AffiliateDto; onEdit: () => void; onClose: () => void }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { commissions, commissionsCursor, payouts, moreCommissions, pay, saving, saveError } = useAffiliatesStore();
  const [paying, setPaying] = useState(false);
  const [method, setMethod] = useState<AffiliatePayoutRequest['method'] | ''>('');
  const [reference, setReference] = useState('');
  const fmt = (halalas: number) => money(halalas, locale, affiliate.currency);
  const link = referralLink(affiliate.code);

  return (
    <Sheet
      open
      size="lg"
      title={affiliate.name}
      subtitle={`${bpsToPercent(affiliate.commissionBps)}% · ?ref=${affiliate.code}`}
      onClose={onClose}
      footer={
        paying ? (
          <>
            <Button variant="secondary" onClick={() => setPaying(false)} disabled={saving}>
              {t('cancel')}
            </Button>
            <Button
              disabled={!method || saving}
              onClick={async () => {
                if (method && (await pay(affiliate.id, { method, reference: reference.trim() || null }))) setPaying(false);
              }}
            >
              {t('affiliatePayConfirm', { amount: fmt(affiliate.stats.approved) })}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onEdit}>
              {t('editEntry')}
            </Button>
            <Button disabled={affiliate.stats.approved <= 0} onClick={() => setPaying(true)}>
              {t('affiliatePay')}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Figure label={t('affiliateVisits')} value={count(affiliate.stats.visits, locale)} />
          <Figure label={t('orders')} value={count(affiliate.stats.orders, locale)} />
          <Figure label={t('affiliateSales')} value={fmt(affiliate.stats.sales)} />
          <Figure label={t('affiliateOwed')} value={fmt(affiliate.stats.approved)} />
        </div>
        <p className="text-xs text-ink-muted">
          {t('affiliatePendingPaid', { pending: fmt(affiliate.stats.pending), paid: fmt(affiliate.stats.paid) })}
        </p>

        <dl className="flex flex-col gap-3">
          <KeyValue label={t('affiliateLink')}>
            <span className="flex min-w-0 items-center gap-1">
              <span dir="ltr" className="truncate text-ink">
                {link}
              </span>
              <CopyButton value={link} label={t('copyLink')} copiedLabel={t('copied')} />
            </span>
          </KeyValue>
          {affiliate.coupon ? <KeyValue label={t('affiliateCoupon')}>{affiliate.coupon.code}</KeyValue> : null}
          {affiliate.account ? (
            <KeyValue label={t('affiliateAccount')}>
              <Link to={`/customers/${affiliate.account.id}`} className="text-brand-700 hover:underline">
                {affiliate.account.name}
              </Link>
            </KeyValue>
          ) : null}
          {affiliate.email ? (
            <KeyValue label={t('email')}>
              <span dir="ltr">{affiliate.email}</span>
            </KeyValue>
          ) : null}
        </dl>

        {paying ? (
          <div className="grid gap-4 rounded-control border border-line p-4 sm:grid-cols-2">
            <Field label={t('refundMethodLabel')} htmlFor="affiliate-pay-method">
              <Select id="affiliate-pay-method" className="w-full" value={method} onChange={(event) => setMethod(event.target.value as typeof method)}>
                <option value="">{t('dispositionPick')}</option>
                {PAYOUT_METHODS.map((entry) => (
                  <option key={entry} value={entry}>
                    {t(`refundMethod${entry}` as MessageKey)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('refundReference')} htmlFor="affiliate-pay-reference" hint={t('refundReferenceHint')}>
              <Input id="affiliate-pay-reference" dir="ltr" maxLength={120} value={reference} onChange={(event) => setReference(event.target.value)} />
            </Field>
          </div>
        ) : null}
        {saveError ? <Alert>{saveError}</Alert> : null}

        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-ink">{t('affiliateCommissions')}</h3>
          {commissions.length === 0 ? (
            <p className="text-sm text-ink-muted">{t('affiliateNoCommissions')}</p>
          ) : (
            <TableScroll>
              <Table>
                <thead>
                  <tr>
                    <Th>{t('affiliateOrder')}</Th>
                    <Th>{t('affiliateSales')}</Th>
                    <Th>{t('affiliateCommission')}</Th>
                    <Th>{t('status')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.map((row) => (
                    <tr key={row.id}>
                      <Td>
                        <Link to={`/orders/${row.order.id}`} dir="ltr" className="text-brand-700 hover:underline">
                          {row.order.number}
                        </Link>
                        <span className="block text-xs text-ink-muted">{row.order.placedAt ? date(row.order.placedAt, locale) : ''}</span>
                      </Td>
                      <Td data-numeric className="whitespace-nowrap text-ink-muted">{fmt(row.base)}</Td>
                      <Td data-numeric className="whitespace-nowrap text-ink">{fmt(row.amount)}</Td>
                      <Td>
                        <Pill tone={COMMISSION_TONE[row.status]}>{t(`commissionStatus_${row.status}` as MessageKey)}</Pill>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableScroll>
          )}
          {commissionsCursor ? (
            <Button variant="ghost" size="sm" className="self-start" onClick={() => void moreCommissions()}>
              {t('loadMore')}
            </Button>
          ) : null}
        </section>

        {payouts.length > 0 ? (
          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold text-ink">{t('affiliatePayouts')}</h3>
            <ul className="divide-y divide-line rounded-control border border-line">
              {payouts.map((payout) => (
                <li key={payout.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <span>
                    <span className="block text-ink">{t(`refundMethod${payout.method}` as MessageKey)}</span>
                    <span className="block text-xs text-ink-muted">
                      {date(payout.createdAt, locale)}
                      {payout.reference ? <span dir="ltr"> · {payout.reference}</span> : null}
                    </span>
                  </span>
                  <span data-numeric className="font-medium text-ink">
                    {fmt(payout.amount)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </Sheet>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-control border border-line px-3 py-2">
      <p className="text-xs text-ink-muted">{label}</p>
      <p data-numeric className="mt-1 text-base font-semibold text-ink">
        {value}
      </p>
    </div>
  );
}
